import { useState } from 'react';
import { Send, Check, ChevronRight } from 'lucide-react';
import { BackButton, PrimaryButton, ClipCard, VideoPlayer } from '@/components/ui';
import { CATEGORIES, COLORS } from '@/data';
import { generateId, now } from '@/lib/utils';
import { d1Upsert } from '@/lib/d1-storage';
import type { Clip, CoupleRecommendation } from '@/types';

interface RecommenderViewProps {
  dateNiteClips: Clip[];
  onBack: () => void;
}

type Step = 'enter-code' | 'browse' | 'preview' | 'sent';

export function RecommenderView({ dateNiteClips, onBack }: RecommenderViewProps) {
  const [step, setStep] = useState<Step>('enter-code');
  const [coupleCode, setCoupleCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [fromName, setFromName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const validateAndProceed = () => {
    const code = coupleCode.trim().toUpperCase();
    if (!code) {
      setCodeError('Please enter a couple code');
      return;
    }
    if (!code.startsWith('VBC-') || code.length !== 10) {
      setCodeError('Invalid code format. Should be VBC-XXXXXX');
      return;
    }
    if (!fromName.trim()) {
      setCodeError('Please enter your name');
      return;
    }
    setCodeError('');
    setStep('browse');
  };

  const clipsInCategory = selectedCategory
    ? dateNiteClips.filter(c => c.categoryId === selectedCategory)
    : [];

  const handleSelectClip = (clip: Clip) => {
    setSelectedClip(clip);
    setStep('preview');
  };

  const sendRecommendation = async () => {
    if (!selectedClip) return;
    setSending(true);

    const recommendation: CoupleRecommendation = {
      id: generateId('rec'),
      coupleId: coupleCode.trim().toUpperCase(),
      clipId: selectedClip.id,
      fromName: fromName.trim(),
      message: message.trim(),
      createdAt: now(),
      seen: false,
    };

    await d1Upsert('couple_recommendations', recommendation);

    setSending(false);
    setStep('sent');
  };

  const category = selectedCategory
    ? CATEGORIES.find(c => c.id === selectedCategory)
    : null;

  // Enter couple code step
  if (step === 'enter-code') {
    return (
      <div className="min-h-screen bg-zinc-900 p-6">
        <BackButton onClick={onBack} />

        <div className="max-w-md mx-auto mt-8">
          <div className="text-center mb-8">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: COLORS.lime }}
            >
              <Send className="w-8 h-8 text-zinc-900" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Recommend a Clip</h1>
            <p className="text-zinc-400">
              Enter a couple's code to recommend content for their next date night
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Your Name</label>
              <input
                type="text"
                value={fromName}
                onChange={e => { setFromName(e.target.value); setCodeError(''); }}
                placeholder="Your name or nickname"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white"
              />
            </div>

            <div>
              <label className="block text-zinc-400 text-sm mb-2">Couple Code</label>
              <input
                type="text"
                value={coupleCode}
                onChange={e => { setCoupleCode(e.target.value.toUpperCase()); setCodeError(''); }}
                onKeyDown={e => e.key === 'Enter' && validateAndProceed()}
                placeholder="VBC-XXXXXX"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white font-mono text-center text-lg tracking-wider"
              />
            </div>

            {codeError && <p className="text-red-400 text-sm">{codeError}</p>}

            <PrimaryButton onClick={validateAndProceed} className="w-full">
              Continue
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  // Browse categories and clips
  if (step === 'browse') {
    if (!selectedCategory) {
      return (
        <div className="min-h-screen bg-zinc-900 p-6">
          <BackButton onClick={() => setStep('enter-code')} />

          <div className="max-w-md mx-auto mt-4">
            <h1 className="text-xl font-bold text-white mb-2">Choose a Category</h1>
            <p className="text-zinc-400 text-sm mb-6">
              Recommending for <span className="text-white font-mono">{coupleCode}</span>
            </p>

            <div className="space-y-2">
              {CATEGORIES.map(cat => {
                const clipCount = dateNiteClips.filter(c => c.categoryId === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className="w-full bg-zinc-800 hover:bg-zinc-750 border border-zinc-700 rounded-xl p-4 flex items-center gap-3 transition-colors"
                  >
                    <span className="text-2xl">{cat.emoji}</span>
                    <div className="flex-1 text-left">
                      <div className="text-white font-medium">{cat.name}</div>
                      <div className="text-zinc-500 text-sm">{clipCount} clips</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-500" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    // Show clips in category
    return (
      <div className="min-h-screen bg-zinc-900 p-6">
        <BackButton onClick={() => setSelectedCategory(null)} />

        <div className="max-w-md mx-auto mt-4">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-3xl">{category?.emoji}</span>
            <div>
              <h1 className="text-xl font-bold text-white">{category?.name}</h1>
              <p className="text-zinc-400 text-sm">{clipsInCategory.length} clips</p>
            </div>
          </div>

          <div className="space-y-3">
            {clipsInCategory.map(clip => (
              <ClipCard
                key={clip.id}
                clip={clip}
                onClick={() => handleSelectClip(clip)}
                variant="list"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Preview and send
  if (step === 'preview' && selectedClip) {
    const cat = CATEGORIES.find(c => c.id === selectedClip.categoryId);

    return (
      <div className="min-h-screen bg-zinc-900 p-6">
        <BackButton onClick={() => { setStep('browse'); setSelectedClip(null); }} />

        <div className="max-w-md mx-auto mt-4">
          <h1 className="text-xl font-bold text-white mb-4">Preview & Send</h1>

          {/* Video preview */}
          <VideoPlayer url={selectedClip.link} className="mb-4" />

          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span>{cat?.emoji}</span>
              <span className="text-white font-medium">{selectedClip.title}</span>
            </div>
            <p className="text-zinc-400 text-sm">{selectedClip.description}</p>
          </div>

          <div className="mb-4">
            <label className="block text-white text-sm font-medium mb-2">
              💬 Add a personal note for the couple
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="You two should definitely try this! I think you'll love how..."
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white resize-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors"
            />
            <p className="text-zinc-500 text-xs mt-1">Your note makes the recommendation more personal!</p>
          </div>

          <PrimaryButton
            onClick={sendRecommendation}
            className="w-full"
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send Recommendation'}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  // Sent confirmation
  if (step === 'sent') {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: COLORS.lime }}
          >
            <Check className="w-10 h-10 text-zinc-900" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Recommendation Sent!</h1>
          <p className="text-zinc-400 mb-8">
            They'll see your recommendation next time they open Date Night
          </p>
          <div className="space-y-3">
            <PrimaryButton
              onClick={() => {
                setSelectedClip(null);
                setMessage('');
                setStep('browse');
                setSelectedCategory(null);
              }}
              className="w-full"
            >
              Send Another
            </PrimaryButton>
            <button
              onClick={onBack}
              className="w-full py-3 text-zinc-400 hover:text-white transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
